import React, { useState } from 'react';
import { Button, Col, Row, Empty, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import type {
  ExtendedHistoryOrderTicketEntity,
  ExtendedHistoryOrderTicketItemEntity,
  HistoryOrderDetailsEntity,
} from 'types/dto/order';
import OrderDetailsItemCell from './OrderDetailsItemCell';
import './OrderDetails.scss';
import OrderStatusTag from 'components/orderStatusTag/OrderStatusTag';
import { BackendLoyaltyItem } from 'types/dto/wallet';
import CouponAndVoucherCell from 'pages/wallet/couponsAndVouchers/subviews/CouponAndVoucherCell';
import CouponAndVoucherDetail from 'pages/wallet/couponAndVoucherDetail/CouponAndVoucherDetail';

const { TabPane } = Tabs;

interface OrderDetailsProps {
  orderData: HistoryOrderDetailsEntity;
  orderItemsExpanded: boolean;
  onToggleOrderItems: () => void;
  voucherExpanded: boolean;
  onToggleVoucher: () => void;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({
  orderData,
  orderItemsExpanded,
  onToggleOrderItems,
  voucherExpanded,
  onToggleVoucher,
}) => {
  const { t } = useTranslation('order_history');
  const { t: tWallet } = useTranslation('wallet');

  const [couponDetailVisibleMap, setCouponDetailVisibleMap] = useState<{ [key: number]: boolean }>({});

  const openCouponDetail = (couponId: number) => {
    setCouponDetailVisibleMap(prev => ({
      ...prev,
      [couponId]: true
    }));
  };

  const closeCouponDetail = (couponId: number) => {
    setCouponDetailVisibleMap(prev => ({
      ...prev,
      [couponId]: false
    }));
  };

  const formatPrice = (price: number) => {
    if (!price || price === 0) {
      return '-';
    }
    return `$${price.toFixed(2)}`;
  };

  const tickets = orderData.tickets || [];
  const loyaltyItems = Array.isArray(orderData?.redeemableTokens)
    ? orderData.redeemableTokens
    : (Array.isArray((orderData as any)?.loyaltyItems) ? (orderData as any).loyaltyItems : []);
  const filteredCoupons = loyaltyItems.filter(
    (item: BackendLoyaltyItem) => !item.code.includes('VOC-')
  );
  const filteredVouchers = loyaltyItems.filter(
    (item: BackendLoyaltyItem) => item.code.includes('VOC-')
  );
  const totalLoyaltyItemsCount = loyaltyItems.length;
  const hasAvailableLoyaltyItems = filteredCoupons.length > 0 || filteredVouchers.length > 0;

  // Backend-provided amounts — all from cus_history_ticket_select (ogh_glu* columns), no F/E calculation
  const subtotal = tickets.reduce((sum: number, ticket: ExtendedHistoryOrderTicketEntity) =>
    sum + (ticket.originalAmount || 0), 0);
  const discountTotal = tickets.reduce((sum: number, ticket: ExtendedHistoryOrderTicketEntity) =>
    sum + (ticket.discountAmount || 0), 0);
  const adjustmentTotal = tickets.reduce((sum: number, ticket: ExtendedHistoryOrderTicketEntity) =>
    sum + (ticket.adjustedAmount || 0), 0);
  const orderTotal = tickets.reduce((sum: number, ticket: ExtendedHistoryOrderTicketEntity) =>
    sum + (ticket.totalAmount || 0), 0);


  const validOrderItemsCount = tickets.reduce((count: number, ticket: ExtendedHistoryOrderTicketEntity) => {
    const validItems = (ticket.items || []).filter(item => (item.qty || 0) > 0);
    return count + validItems.length;
  }, 0);

  const renderReadOnlyLoyaltyList = (
    items: BackendLoyaltyItem[],
    type: 'coupons' | 'vouchers'
  ) => {
    if (items.length === 0) {
      return (
        <Empty
          description={
            type === 'coupons'
              ? tWallet('noCouponsAvailable')
              : tWallet('noVouchersAvailable')
          }
          className="loyalty-empty-state"
        />
      );
    }

    return (
      <div className="coupon-grid">
        {items.map((item) => (
          <React.Fragment key={item.id}>
            <div
              className="coupon-card-wrapper"
              onClick={() => openCouponDetail(item.id)}
              style={{
                cursor: 'pointer',
                marginBottom: '16px'
              }}
            >
              <CouponAndVoucherCell
                coupon={item}
                needUseButton={false}
                // isExpiredOrUsed={!!item.used_at.trim()}
              />
            </div>

            <CouponAndVoucherDetail
              visible={!!couponDetailVisibleMap[item.id]}
              onClose={() => closeCouponDetail(item.id)}
              coupon={item}
            />
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="order-details-container">
      <div className="vls-section-title">{t('orderDetails')}</div>
      <div className="status-tag-container">
        <OrderStatusTag status={orderData.oph_sts} statusName={orderData.status} />
      </div>

      {/* Order Summary */}
      <Row className="order-summary">
        <Col className="order-number" xs={24} md={12}>
          {orderData.reservationNo || 'N/A'}
          <div className="small-size-status-tag">
            <OrderStatusTag status={orderData.oph_sts} statusName={orderData.status} />
          </div>
        </Col>

        <Col className="items-list" xs={24} md={12}>
          <div className="price-breakdown">
            {subtotal > 0 && (
              <div className="price-row">
                <span className="price-label">{t('subtotal')}</span>
                <span className="price-value">{formatPrice(subtotal)}</span>
              </div>
            )}
            <div className="price-row">
              <span className="price-label">{t('discount')}</span>
              <span className="price-value">
                {discountTotal < 0
                  ? `-$${Math.abs(discountTotal).toFixed(2)}`
                  : `$${discountTotal.toFixed(2)}`}
              </span>
            </div>
            {adjustmentTotal !== 0 && (
              <div className="price-row">
                <span className="price-label">{t('adjustment')}</span>
                <span className="price-value">
                  {adjustmentTotal < 0
                    ? `-$${Math.abs(adjustmentTotal).toFixed(2)}`
                    : `$${adjustmentTotal.toFixed(2)}`}
                </span>
              </div>
            )}
            {orderTotal > 0 && (
              <div className="price-row total-row">
                <span className="price-label total-label">{t('totalAmount')}</span>
                <span className="price-value total-value">{formatPrice(orderTotal)}</span>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Voucher Section */}
      <div className="voucher-section">
        <div className="voucher-row" onClick={onToggleVoucher}>
          <span className="voucher-label">
            {t('voucherCoupon')} ({totalLoyaltyItemsCount})
          </span>


          {totalLoyaltyItemsCount > 0 && (
            <Button
              type="link"
              className="toggle-details-button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleVoucher();
              }}
            >
              {voucherExpanded ? t('hideDetails') : t('showDetails')}
            </Button>
          )}
        </div>

        {voucherExpanded && (
          <div className="voucher-expandable-list">
            <div className="voucher-loyalty-details">
              {hasAvailableLoyaltyItems && (
                <Tabs
                  defaultActiveKey="vouchers"
                  className="loyalty-tabs"
                  tabBarGutter={16}
                  size="large"
                >
                  {filteredVouchers.length > 0 && (
                    <TabPane
                      tab={`${tWallet('vouchers')} (${filteredVouchers.length})`}
                      key="vouchers"
                    >
                      {renderReadOnlyLoyaltyList(filteredVouchers, 'vouchers')}
                    </TabPane>
                  )}
                  {filteredCoupons.length > 0 && (
                    <TabPane
                      tab={`${tWallet('coupons')} (${filteredCoupons.length})`}
                      key="coupons"
                    >
                      {renderReadOnlyLoyaltyList(filteredCoupons, 'coupons')}
                    </TabPane>
                  )}
                </Tabs>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Order Items Section */}
      <div className="order-items-section">
        <div className="order-items-header" onClick={onToggleOrderItems}>
          <span className="order-items-label">
            {t('orderItems')} ({validOrderItemsCount})
          </span>


          {validOrderItemsCount > 0 && (
            <Button
              type="link"
              className="toggle-details-button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleOrderItems();
              }}
            >
              {orderItemsExpanded ? t('hideDetails') : t('showDetails')}
            </Button>
          )}
        </div>

        {orderItemsExpanded && (
          <div className="order-items-list">
            {tickets.map((ticket: ExtendedHistoryOrderTicketEntity) => {
              const visibleItems = (ticket.items || []).filter(item => (item.qty || 0) > 0);

              if (visibleItems.length === 0) return null;

              return (
                  <React.Fragment key={ticket.ticketID}>
                    <div className="ticket-header">
                      {t('ticketNumber')}: {ticket.ticketNo || 'N/A'}
                    </div>
                    {visibleItems.map((item: ExtendedHistoryOrderTicketItemEntity, itemIndex: number) => {
                      // Item numbering restarts at 1 for each GLUS ticket
                      const currentItemNumber = itemIndex + 1;
                      return (
                        <OrderDetailsItemCell
                          key={`${ticket.ticketID}-${item.itemID}-${itemIndex}`}
                          item={item}
                          itemNumber={currentItemNumber}
                          ticketNumber={ticket.ticketNo || ''}
                        />
                      );
                    })}
                    {((ticket.promotions?.length ?? 0) > 0 || (ticket.redeems?.length ?? 0) > 0) && (
                      <div className="others-section">
                        {(ticket.promotions || []).map((p, idx) => (
                          <div key={`p-${idx}`} className="others-row">
                            <span className="others-label">{t('promotion')} ({p.seq ?? (idx + 1)})</span>
                            <span className="others-desc">{p.desc}</span>
                            <span className="others-amount">
                              {(p.amount ?? 0) < 0
                                ? `-$${Math.abs(p.amount ?? 0).toFixed(2)}`
                                : `$${(p.amount ?? 0).toFixed(2)}`}
                            </span>
                          </div>
                        ))}
                        {(ticket.redeems || []).map((r, idx) => (
                          <div key={`r-${idx}`} className="others-row">
                            <span className="others-label">{t('others')} ({r.seq ?? (idx + 1)})</span>
                            <span className="others-desc">{r.desc}</span>
                            <span className="others-amount">
                              {(r.amount ?? 0) < 0
                                ? `-$${Math.abs(r.amount ?? 0).toFixed(2)}`
                                : `$${(r.amount ?? 0).toFixed(2)}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetails;